/**
 * Sysadmin Runtime → Storage (Phase 5 PR-D v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.6 + Phase 5 architect §2.4:
 *   Storage sub-tab surfaces Postgres + Qdrant + Valkey usage so the
 *   operator can spot disk pressure / replication lag / memory creep
 *   without leaving the sysadmin shell. Per-collection Qdrant
 *   breakdown is the most operationally useful piece — collections-
 *   per-tenant means a tenant's vector growth shows up here before it
 *   shows up in their bill.
 *
 * Refresh: 10s default.
 */

import { createFileRoute } from '@tanstack/react-router';
import {
  Database,
  RefreshCw,
  Clock,
  AlertTriangle,
  HardDrive,
  Layers,
  Hash,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { PageError } from '@/components/ui/page-error';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useSysadminRuntimeStorage,
} from '@/lib/use-sysadmin-runtime-ops';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/runtime/storage')({
  component: StoragePage,
});

const REPLICATION_WARN_SECONDS = 60;

function StoragePage() {
  const { data, error, refetch, isFetching, dataUpdatedAt } =
    useSysadminRuntimeStorage();

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load storage snapshot"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      </div>
    );
  }

  const postgres = data?.postgres;
  const qdrant = data?.qdrant;
  const valkey = data?.valkey;
  const replicationLag = postgres?.replicationLagSeconds ?? null;

  // Bug #892 — don't render the 4-up StatTile grid with fallback "—" values
  // when the initial probe hasn't returned. Operators previously read the
  // dashes as real (i.e. "Postgres has no data") instead of the loading
  // state. Hold the page on a single loading surface until `data` lands.
  if (!data) {
    return (
      <div className="p-6 max-w-6xl mx-auto space-y-5">
        <StorageHeader
          dataUpdatedAt={dataUpdatedAt}
          isFetching={isFetching}
          onRefresh={() => refetch()}
        />
        <div
          role="status"
          aria-live="polite"
          className="py-6 text-center text-muted-foreground text-sm animate-pulse"
        >
          Probing storage…
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <StorageHeader
        dataUpdatedAt={dataUpdatedAt}
        isFetching={isFetching}
        onRefresh={() => refetch()}
      />

      {/* ── 4-up summary ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Postgres size"
          value={postgres?.databaseSizeHuman ?? '—'}
          icon={Database}
          tone={postgres?.available ? 'default' : 'destructive'}
          hint={postgres?.databaseName ?? 'unknown DB'}
        />
        <StatTile
          label="Qdrant points"
          value={qdrant?.totalPoints?.toLocaleString() ?? '—'}
          suffix={
            qdrant?.collectionCount != null
              ? ` (${qdrant.collectionCount} cols)`
              : undefined
          }
          icon={Layers}
          tone={qdrant?.available ? 'default' : 'destructive'}
          hint={qdrant?.probeError ?? undefined}
        />
        <StatTile
          label="Valkey memory"
          value={valkey?.memoryUsedHuman ?? '—'}
          icon={Hash}
          tone={
            valkey?.available
              ? 'default'
              : valkey?.probeError?.includes('not configured')
                ? 'muted'
                : 'destructive'
          }
          hint={
            valkey?.keyCount != null
              ? `${valkey.keyCount.toLocaleString()} keys`
              : (valkey?.probeError ?? undefined)
          }
        />
        <StatTile
          label="Replication lag"
          value={
            replicationLag !== null
              ? `${replicationLag.toFixed(1)}s`
              : 'n/a'
          }
          icon={HardDrive}
          tone={
            replicationLag === null
              ? 'muted'
              : replicationLag > REPLICATION_WARN_SECONDS
                ? 'destructive'
                : replicationLag > 5
                  ? 'warning'
                  : 'success'
          }
          hint={
            replicationLag === null
              ? 'Single-node deployment'
              : replicationLag > REPLICATION_WARN_SECONDS
                ? 'Replica is falling behind'
                : 'Replica in sync'
          }
        />
      </div>

      {/* ── Postgres detail ── */}
      {postgres && (
            <section className="space-y-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                PostgreSQL
                {!postgres.available && (
                  <StatusPill status="destructive" compact icon={AlertTriangle}>
                    Probe failed
                  </StatusPill>
                )}
                {replicationLag !== null && replicationLag > REPLICATION_WARN_SECONDS && (
                  <StatusPill status="destructive" compact icon={AlertTriangle}>
                    Replication lag {replicationLag.toFixed(0)}s
                  </StatusPill>
                )}
              </h2>
              <div className="rounded-md border p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <KvRow label="Database" value={postgres.databaseName ?? '—'} mono />
                <KvRow
                  label="Size"
                  value={postgres.databaseSizeHuman ?? '—'}
                  mono
                />
                <KvRow label="WAL LSN" value={postgres.walLsn ?? '—'} mono />
                <KvRow
                  label="Replication lag"
                  value={
                    replicationLag !== null
                      ? `${replicationLag.toFixed(2)}s`
                      : 'n/a (single-node)'
                  }
                  mono
                />
              </div>
              {postgres.probeError && (
                <div className="text-[11px] text-destructive bg-destructive/10 rounded p-2">
                  {postgres.probeError}
                </div>
              )}
            </section>
          )}

          {/* ── Qdrant detail ── */}
          {qdrant && (
            <section className="space-y-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                Qdrant
                {!qdrant.available && (
                  <StatusPill status="destructive" compact icon={AlertTriangle}>
                    Probe failed
                  </StatusPill>
                )}
              </h2>
              {qdrant.probeError && (
                <div className="text-[11px] text-destructive bg-destructive/10 rounded p-2">
                  {qdrant.probeError}
                </div>
              )}
              {qdrant.collections.length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                  No collections to report.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Collection</TableHead>
                      <TableHead className="text-right">Points</TableHead>
                      <TableHead className="text-right">Vectors</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qdrant.collections.map((c) => (
                      <TableRow key={c.name}>
                        <TableCell className="text-xs font-mono">
                          {c.name}
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono tabular-nums">
                          {c.pointsCount?.toLocaleString() ?? '—'}
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono tabular-nums">
                          {c.vectorsCount?.toLocaleString() ?? '—'}
                        </TableCell>
                        <TableCell>
                          {c.status === 'green' ? (
                            <StatusPill status="healthy" compact>{c.status}</StatusPill>
                          ) : c.status === 'yellow' ? (
                            <StatusPill status="warning" compact>{c.status}</StatusPill>
                          ) : c.status === 'red' ? (
                            <StatusPill status="destructive" compact>{c.status}</StatusPill>
                          ) : (
                            <StatusPill status="muted" compact>{c.status ?? '—'}</StatusPill>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </section>
          )}

          {/* ── Valkey detail ── */}
          {valkey && (
            <section className="space-y-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                Valkey / Redis
                {!valkey.available && (
                  <StatusPill
                    status={
                      valkey.probeError?.includes('not configured')
                        ? 'muted'
                        : 'destructive'
                    }
                    compact
                    icon={AlertTriangle}
                  >
                    {valkey.probeError?.includes('not configured')
                      ? 'Not configured'
                      : 'Probe failed'}
                  </StatusPill>
                )}
              </h2>
              <div className="rounded-md border p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <KvRow
                  label="Memory used"
                  value={valkey.memoryUsedHuman ?? '—'}
                  mono
                />
                <KvRow
                  label="Keys"
                  value={
                    valkey.keyCount != null
                      ? valkey.keyCount.toLocaleString()
                      : '—'
                  }
                  mono
                />
              </div>
              {valkey.probeError &&
                !valkey.probeError.includes('not configured') && (
                  <div className="text-[11px] text-destructive bg-destructive/10 rounded p-2">
                    {valkey.probeError}
                  </div>
                )}
            </section>
          )}
    </div>
  );
}

// ─── Atoms ───────────────────────────────────────────────────────────────

function StorageHeader({
  dataUpdatedAt,
  isFetching,
  onRefresh,
}: {
  dataUpdatedAt: number;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <Database className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Storage</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">§3.6</Badge>
            <Badge variant="secondary" className="text-xs">PR-D</Badge>
            {dataUpdatedAt > 0 && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching}>
        <RefreshCw
          className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')}
        />
        Refresh
      </Button>
    </div>
  );
}

function KvRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn('text-sm', mono && 'font-mono')}>{value}</div>
    </div>
  );
}
